/** Field schemas shared by several contracts. */
import { getCountries, getCountryCallingCode, isValidPhoneNumber, parsePhoneNumberFromString, } from "libphonenumber-js";
import { z } from "zod";
import { COUNTRY_NAMES_AR } from "./countries.js";
import { TIMEZONE_COUNTRY } from "./timezones.js";
/**
 * Phone numbers are stored in E.164 (+9665XXXXXXXX). People type them many ways (owner request 2026-10-21,
 * like Adahina): with or without the country code, with + or 00 or neither, with a leading 0 or not, with
 * Arabic-Indic digits, spaces or dashes. The number is read for the chosen country (Saudi Arabia by default)
 * and checked with libphonenumber-js, which knows every country's numbering plan.
 */
export function normalizePhone(input, country = "SA") {
    const s = input
        .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
        .replace(/[\u06f0-\u06f9]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
        .replace(/[\s\-().\u200e\u200f\u202a-\u202e]/g, "");
    const international = s.startsWith("+") ? s : s.startsWith("00") ? `+${s.slice(2)}` : null;
    const candidates = international
        ? [parsePhoneNumberFromString(international)]
        : [
            parsePhoneNumberFromString(s, country),
            // The country code typed without + (9665XXXXXXXX), for any country.
            /^\d{8,15}$/.test(s) ? parsePhoneNumberFromString(`+${s}`) : undefined,
        ];
    const valid = candidates.find((p) => p?.isValid());
    return valid ? valid.number : (international ?? s);
}
export const PHONE_COUNTRIES = getCountries()
    .map((iso) => ({ iso, dial: getCountryCallingCode(iso), name: COUNTRY_NAMES_AR[iso] ?? iso }))
    .sort((a, b) => (a.iso === "SA" ? -1 : b.iso === "SA" ? 1 : a.name.localeCompare(b.name, "ar")));
/** A country's flag from its ISO code (regional indicator symbols). */
export const countryFlag = (iso) => String.fromCodePoint(...[...iso.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
/** The parts of a stored number for the phone field: its country and the national number. */
export function splitPhone(e164) {
    const p = e164 ? parsePhoneNumberFromString(e164) : undefined;
    return p?.country ? { country: p.country, national: p.nationalNumber } : null;
}
/** The country of an IANA time zone such as "Asia/Riyadh", when it is one the phone field offers. */
export function countryOfTimeZone(timeZone) {
    const iso = timeZone ? TIMEZONE_COUNTRY[timeZone] : undefined;
    return iso && PHONE_COUNTRIES.some((c) => c.iso === iso) ? iso : null;
}
/** The country of a locale such as "ar-SA", when it is one the phone field offers. */
export function countryOfLocale(locale) {
    const region = locale?.match(/[-_]([A-Z]{2})\b/)?.[1];
    return region && PHONE_COUNTRIES.some((c) => c.iso === region) ? region : null;
}
export const phone = z
    .string()
    .transform((v) => normalizePhone(v))
    .pipe(z
    .string()
    .refine((v) => /^\+[1-9][0-9]{7,14}$/.test(v) && isValidPhoneNumber(v), "رقم الجوال غير صحيح؛ اكتبه مثل 05XXXXXXXX"));
export const password = z.string().min(8, "كلمة المرور 8 أحرف على الأقل").max(128);
export const Gender = z.enum(["male", "female"]);
/** A stored phone as people write it: 05XXXXXXXX for Saudi mobiles, otherwise the international form. */
export function displayPhone(e164) {
    if (!e164)
        return "";
    return /^\+9665\d{8}$/.test(e164) ? `0${e164.slice(4)}` : e164;
}
//# sourceMappingURL=fields.js.map